import { type FC, lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { type FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ErrorProvider, Errors, type IError } from '@wf/sw-ui-kit';
import { Mode, Tabs } from '@wf/components';

import { type IAttachmentStateItem } from '../../components/Attachments/types';
import ItemsContextProvider from '../../components/Items/ItemsContextProvider';
import Beneficiaries from '../../components/Beneficiaries/Beneficiaries';
import ContentContainer from '../../../../components/ContentContainer';
import Attachments from '../../components/Attachments/Attachments';
import NamesAndParties from '../../components/NamesAndParties';
import { type ISavedItem } from '../../components/Items/types';
import { LicenseModeContext } from '../../LicenseModeContext';
import LicenseHeader from '../../components/LicenseHeader';
import TutorialLink from '../../components/TutorialLink';
import Consignment from '../../components/Consignment';
import HeaderTab from '../../components/HeaderTab';
import Loader from '../../../../components/Loader';
import Fees from '../../components/Fees';

import { ArrayFields, HeaderTabFields, NamesAndPartiesTabFields } from '../../types/fieldEnums';
import { useCompanyAndDeclarantDetails } from '../../hooks/useCompanyAndDeclarantDetails';
import useLicenseCleanupFunction from '../../hooks/useLicenseCleanupFunction';
import useLicenseTypeNature from '../../../../hooks/useLicenseTypeNature';
import useValidationSchema from '../../hooks/useValidationSchema';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import useGenerateParams from '../../hooks/useGenerateParams';
import useQueryParam from '../../../../hooks/useQueryParam';
import { ItemsQueryParams } from '../../../../constants';
import useRedirect from '../../../../hooks/useRedirect';
import { LicenseTypeNature } from '../../../../types';
import useTabIndexes from '../../hooks/useTabIndexes';
import useTabErrors from '../../hooks/useTabErrors';

import {
  errorsSelector,
  fieldConfigsSelector,
  hasBackendErrorSelector,
  lpcoSelector,
  parentLicenseTypeSelector,
  parentLpcoSelector,
  selectedLicenseTypeSelector,
  userReferenceNumberSelector,
} from '../../store/selectors';
import { getLicenseTypeByCode, getLpcoFormDetails, getParentLpco, getUserReferenceNumber } from '../../store/actions';
import getFormValuesFromLicense, { getFormValuesFromLicenseType } from '../../helpers/getFormValues';
import { getFormValuesFromParentLicense } from '../../helpers/getFormValuesFromParent';

import styles from './License.module.scss';

const Items = lazy(
  () =>
    import(process.env.REACT_APP_AI_OGA_ENABLED === 'true' ? '../../components/ItemsWithAI' : '../../components/Items')
);

interface ILicenseProps {
  mode: Mode;
}

const License: FC<ILicenseProps> = ({ mode }) => {
  const schema = useValidationSchema();
  const form = useForm({
    resolver: yupResolver(schema),
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id = '' } = useParams();
  const dispatch = useDispatch();
  useRedirect();

  const hasBackendErrors = useSelector(hasBackendErrorSelector);
  const backendErrors = useSelector(errorsSelector);

  const tabIndexes = useTabIndexes();
  const { parentId, childLicenseTypeCode } = useGenerateParams();

  const {
    reset,
    setValue,
    formState: { errors, isSubmitted },
    trigger,
  } = form;

  const { companyFieldsAreDisabled, declarantFieldAreDisabled, companyOptions } = useCompanyAndDeclarantDetails(
    setValue,
    mode
  );

  const { data: lpco, loading: lpcoLoading, isFetched } = useSelector(lpcoSelector);
  const { data: parentLpco, loading: parentLpcoLoading } = useSelector(parentLpcoSelector);
  const parentLicenseType = useSelector(parentLicenseTypeSelector);

  const { data: selectedLicenseType, loading: licenseTypeLoading } = useSelector(selectedLicenseTypeSelector);
  const { tutorialUrl, licenseTypeCode, licenseTypeNature } = selectedLicenseType?.licenseTypeResponse ?? {};

  const fieldConfigs = useSelector(fieldConfigsSelector);

  const userReferenceNumber = useSelector(userReferenceNumberSelector);

  const [savedItems, setSavedItems] = useState<ISavedItem[]>([]);
  const [savedAttachments, setSavedAttachments] = useState<IAttachmentStateItem[]>([]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(tabIndexes.HEADER);

  const natureIsEPhyto = licenseTypeNature === LicenseTypeNature.EPHYTO;
  const isEPhytoRoute = useLicenseTypeNature() === LicenseTypeNature.EPHYTO;
  const showConsignmentTab = natureIsEPhyto || isEPhytoRoute;

  const tabErrors = useTabErrors(errors);

  const [itemIndex, setItemIndex] = useQueryParam(ItemsQueryParams.itemIndex);

  useEffect(() => {
    if (itemIndex !== null) {
      if (mode === Mode.create) {
        setItemIndex(null);
      } else {
        setSelectedTabIndex(Number(tabIndexes.ITEMS));
      }
    }
  }, []);

  useEffect(() => {
    if ((mode === Mode.update || mode === Mode.view) && id) {
      dispatch(getLpcoFormDetails(Number(id)));
    } else {
      dispatch(getUserReferenceNumber());

      if (parentId && childLicenseTypeCode) {
        dispatch(getParentLpco(Number(parentId)));
        dispatch(getLicenseTypeByCode({ licenseTypeCode: childLicenseTypeCode, generateConfiguration: true }));
      }
    }
  }, [id]);

  useEffect(() => {
    if (userReferenceNumber) {
      setValue(HeaderTabFields.userReferenceNumber, userReferenceNumber);
    }
  }, [setValue, userReferenceNumber]);

  // initialize form values for create mode without parent License
  useEffect(() => {
    if (mode === 'create' && !parentId) {
      const getValuesFromPrevLicenceType = (prev: FieldValues) => ({
        [HeaderTabFields.userReferenceNumber]: prev.userReferenceNumber,
        ...(companyFieldsAreDisabled && {
          [NamesAndPartiesTabFields.companyCode]: prev.companyCode,
          [NamesAndPartiesTabFields.companyAddress]: prev.companyAddress,
          [NamesAndPartiesTabFields.companyEmail]: prev.companyEmail,
          [NamesAndPartiesTabFields.companyPhone]: prev.companyPhone,
        }),
        ...(declarantFieldAreDisabled && {
          [NamesAndPartiesTabFields.declarantCode]: prev.declarantCode,
          [NamesAndPartiesTabFields.declarantName]: prev.declarantName,
        }),
      });

      if (!lpco) {
        if (selectedLicenseType) {
          reset((prev) => ({
            [HeaderTabFields.licenseType]: prev.licenseType,
            ...getValuesFromPrevLicenceType(prev),
            ...getFormValuesFromLicenseType(selectedLicenseType.licenseTypeResponse),
          }));
        } else {
          reset((prev) => getValuesFromPrevLicenceType(prev));
        }
      } else if (lpco && selectedLicenseType) {
        //imported data
        reset((prev) => ({
          ...getFormValuesFromLicense(lpco, selectedLicenseType.licenseTypeResponse),
          ...getValuesFromPrevLicenceType(prev),
        }));
      }
    }
  }, [companyFieldsAreDisabled, declarantFieldAreDisabled, lpco, mode, parentId, reset, selectedLicenseType]);

  // initialize form values in create mode with parent License values;
  useEffect(() => {
    if (mode === Mode.create && parentId) {
      if (parentLpco && selectedLicenseType && parentLicenseType) {
        reset({
          userReferenceNumber,
          ...getFormValuesFromParentLicense({
            parentLpco,
            licenseType: selectedLicenseType.licenseTypeResponse,
            licenseTypeFieldConfigs: fieldConfigs,
            parentLicenseType: parentLicenseType.licenseTypeResponse,
          }),
        });
      }
    }
  }, [mode, parentId, parentLicenseType, parentLpco, reset, selectedLicenseType, userReferenceNumber]);

  // initialize form values with license values
  useEffect(() => {
    if (lpco?.id && selectedLicenseType) {
      reset({
        ...getFormValuesFromLicense(lpco, selectedLicenseType.licenseTypeResponse),
      });
    }
  }, [lpco, reset, selectedLicenseType]);

  const cleanupFunction = useLicenseCleanupFunction();

  useEffect(() => cleanupFunction, [cleanupFunction]);

  // translate errors when changing language
  useUpdateEffect(() => {
    void (isSubmitted && trigger());
  }, [language]);

  const showFeesTab = !!lpco?.fees.length;

  const getFieldShouldFocus = useCallback(
    (error: IError) =>
      !(
        error.fieldName.includes(ArrayFields.items) ||
        error.fieldName.includes(ArrayFields.attachedDocuments) ||
        error.fieldName.includes(ArrayFields.beneficiaries)
      ),
    []
  );

  return (
    <LicenseModeContext.Provider value={mode}>
      <FormProvider {...form}>
        <ErrorProvider
          getFieldShouldFocus={getFieldShouldFocus}
          setSelectedTabIndex={setSelectedTabIndex}
          selectedTabIndex={selectedTabIndex}
          errors={backendErrors}
        >
          <LicenseHeader id={id} />
          <ContentContainer>
            <Tabs
              onSelect={(index) => setSelectedTabIndex(index)}
              selectedIndex={selectedTabIndex}
              className={styles.tabContainer}
              forceRenderTabPanel
            >
              <Tabs.TabList>
                <Tabs.Tab hasError={tabErrors.header}>{t('Header')}</Tabs.Tab>
                <Tabs.Tab hasError={tabErrors.namesAndParties}>{t('Names & Parties')}</Tabs.Tab>
                {showConsignmentTab && <Tabs.Tab hasError={tabErrors.consignment}>{t('Consignment')}</Tabs.Tab>}
                <Tabs.Tab hasError={tabErrors.items} count={savedItems.length}>
                  {t('Items')}
                </Tabs.Tab>
                <Tabs.Tab hasError={tabErrors.attachedDocuments} count={savedAttachments.length}>
                  {t('Attached Documents')}
                </Tabs.Tab>
                {showFeesTab && <Tabs.Tab hasError={tabErrors.fees}>{t('Fees')}</Tabs.Tab>}
              </Tabs.TabList>
              <Tabs.TabPanel>
                <HeaderTab />
              </Tabs.TabPanel>
              <Tabs.TabPanel className={styles.namesAndPartiesTab}>
                <NamesAndParties
                  declarantFieldAreDisabled={declarantFieldAreDisabled}
                  companyFieldsAreDisabled={companyFieldsAreDisabled}
                  companyOptions={companyOptions}
                />
                <Beneficiaries />
              </Tabs.TabPanel>
              {showConsignmentTab && (
                <Tabs.TabPanel>
                  <Consignment />
                </Tabs.TabPanel>
              )}
              <Tabs.TabPanel className={styles.itemsTab}>
                <Suspense fallback={null}>
                  <ItemsContextProvider>
                    {(isFetched || mode === Mode.create) && (
                      <Items savedItems={savedItems} setSavedItems={setSavedItems} />
                    )}
                  </ItemsContextProvider>
                </Suspense>
              </Tabs.TabPanel>
              <Tabs.TabPanel className={styles.itemsTab}>
                <Attachments savedAttachments={savedAttachments} setSavedAttachments={setSavedAttachments} />
              </Tabs.TabPanel>
              {showFeesTab && (
                <Tabs.TabPanel>
                  <Fees />
                </Tabs.TabPanel>
              )}
            </Tabs>
            {(lpcoLoading || licenseTypeLoading || parentLpcoLoading) && <Loader />}
          </ContentContainer>
          {hasBackendErrors && <Errors errors={backendErrors} />}
          {mode !== Mode.view && tutorialUrl && licenseTypeCode && (
            <TutorialLink url={tutorialUrl} licenseTypeCode={licenseTypeCode} />
          )}
        </ErrorProvider>
      </FormProvider>
    </LicenseModeContext.Provider>
  );
};

export default License;
